import { useCallback, useEffect, useRef, useState } from 'react'

import {
	drawCrop,
	encodeSource,
	loadFileImage,
	MAX_ORIGINAL_BYTES,
	type PreparedImage,
} from '#app/utils/image.client.ts'

const BOX_DISPLAY_WIDTH = 480
const MAX_BOX_HEIGHT = 480
const MAX_ZOOM = 3
/** iPhone pictures may be HEIC; Windows only lists them when asked by name */
const ACCEPT = 'image/*,.heic,.heif'

export type CropConfig =
	/** Framing UI: fixed aspect, drawn to cover the box, safe overlay */
	| { mode: 'crop'; aspect: number; circle?: boolean }
	/** No aspect: just resize+compress the original, nothing can get cut */
	| { mode: 'free' }

type ImageCropperProps = {
	crop: CropConfig
	onConfirm: (result: PreparedImage) => void
	onClose: () => void
	/** a picture the user already chose: start framing it straight away */
	initialFile?: File | null
}

/**
 * Modal where the user picks an image and (in crop mode) positions it with
 * pan+zoom inside the target frame. The safe-zone overlay shows what's
 * guaranteed visible in every display context. Confirming returns the
 * already-resized/compressed file ready to submit with the normal form.
 */
export function ImageCropper({
	crop,
	onConfirm,
	onClose,
	initialFile,
}: ImageCropperProps) {
	const [img, setImg] = useState<HTMLImageElement | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [zoom, setZoom] = useState(1.25)
	const [offsetX, setOffsetX] = useState(0)
	const [offsetY, setOffsetY] = useState(0)
	const [busy, setBusy] = useState(false)
	const [opening, setOpening] = useState(false)
	const panState = useRef<{ x: number; y: number } | null>(null)
	const objectUrlRef = useRef<string | null>(null)
	/** the picture `img` shows, kept as the last-resort upload */
	const blobRef = useRef<Blob | null>(null)

	const revoke = () => {
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current)
			objectUrlRef.current = null
		}
	}
	useEffect(revoke, [])

	const clampOffsets = (cx: number, cy: number) => {
		setOffsetX(Math.max(-1, Math.min(1, cx)))
		setOffsetY(Math.max(-1, Math.min(1, cy)))
	}

	const handleFile = async (newFile: File) => {
		setError(null)
		setOpening(true)
		try {
			revoke()
			const loaded = await loadFileImage(newFile)
			objectUrlRef.current = loaded.img.src
			blobRef.current = loaded.blob
			setImg(loaded.img)
			setZoom(1.25)
			setOffsetX(0)
			setOffsetY(0)
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Kunne ikkje lese biletet')
		} finally {
			setOpening(false)
		}
	}

	// a file picked before the dialog opened: no second trip to the picker
	const handleFileRef = useRef(handleFile)
	handleFileRef.current = handleFile
	useEffect(() => {
		if (initialFile) void handleFileRef.current(initialFile)
	}, [initialFile])

	const previewCanvas = useCallback(
		(node: HTMLCanvasElement | null) => {
			if (!node) return
			if (!img) return
			if (crop.mode === 'free') return
			const canvas = drawCrop(img, {
				boxWidth: BOX_DISPLAY_WIDTH,
				aspect: crop.aspect,
				zoom,
				offsetX,
				offsetY,
			})
			node.width = canvas.width
			node.height = canvas.height
			node.getContext('2d')?.drawImage(canvas, 0, 0)
		},
		[crop, img, offsetX, offsetY, zoom],
	)

	// In free mode the modal only resizes/compresses — no crop preview needed

	const onPointerDown = (e: React.PointerEvent) => {
		if (crop.mode === 'free') return
		const target = e.currentTarget as HTMLElement
		target.setPointerCapture(e.pointerId)
		panState.current = { x: e.clientX, y: e.clientY }
	}

	const onPointerMove = (e: React.PointerEvent) => {
		if (!panState.current) return
		const dx = e.clientX - panState.current.x
		const dy = e.clientY - panState.current.y
		panState.current = { x: e.clientX, y: e.clientY }
		// relative to the box as shown: on a phone it is narrower than 480 px
		const box = e.currentTarget.getBoundingClientRect()
		clampOffsets(
			offsetX + (dx / box.width) * 2,
			offsetY + (dy / box.height) * 2,
		)
	}

	const onPointerUp = () => {
		panState.current = null
	}

	const confirm = async () => {
		if (!img) return
		setBusy(true)
		try {
			if (crop.mode === 'crop') {
				const canvas = drawCrop(img, {
					boxWidth: Math.min(BOX_DISPLAY_WIDTH * 2, img.naturalWidth),
					aspect: crop.aspect,
					zoom,
					offsetX,
					offsetY,
				})
				onConfirm(await encodeSource(canvas))
			} else {
				onConfirm(await encodeSource(img).catch(() => asIs(img)))
			}
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Klarte ikkje å førebu biletet',
			)
			setBusy(false)
		}
	}

	// nothing to crop: the server can shrink and convert the picture itself
	const asIs = (shown: HTMLImageElement): PreparedImage => {
		const blob = blobRef.current
		if (!blob || blob.size > MAX_ORIGINAL_BYTES) {
			throw new Error('Klarte ikkje å førebu biletet. Prøv eit anna bilete.')
		}
		return {
			file: new File([blob], 'bilete', { type: blob.type }),
			width: shown.naturalWidth,
			height: shown.naturalHeight,
		}
	}

	return (
		<div
			className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
			onPointerDown={(e) => {
				if (e.target === e.currentTarget) onClose()
			}}
		>
			<div className="bg-base-100 max-h-full w-full max-w-xl overflow-y-auto rounded-xl p-4 shadow-2xl">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-bold">Bilete</h3>
					<button
						type="button"
						className="btn btn-ghost btn-sm"
						onClick={onClose}
					>
						Avbryt
					</button>
				</div>

				{!img ? (
					<div className="grid w-full place-items-center gap-4 p-4">
						{opening ? (
							<p role="status" className="text-base-content/70">
								Opnar biletet…
							</p>
						) : (
							<label className="btn">
								Vel bilete
								<input
									type="file"
									accept={ACCEPT}
									className="hidden"
									onChange={(e) => {
										const newFile = e.target.files?.[0]
										if (newFile) void handleFile(newFile)
									}}
								/>
							</label>
						)}
						{error && <p className="text-error text-center">{error}</p>}
						<p className="text-base-content/60 text-sm">
							Biletet blir beskore og komprimert lokalt i nettlesaren før det
							blir lagra.
						</p>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						<div
							onPointerDown={onPointerDown}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerCancel={onPointerUp}
							className={`bg-base-200 relative mx-auto w-full overflow-hidden rounded-xl ${crop.mode === 'free' ? '' : 'cursor-grab touch-none active:cursor-grabbing'}`}
							style={
								crop.mode === 'free'
									? { maxWidth: BOX_DISPLAY_WIDTH, maxHeight: MAX_BOX_HEIGHT }
									: {
											// keeps the crop's shape at any width, so the preview
											// fills it and the safe circle stays round
											aspectRatio: crop.aspect,
											maxWidth: Math.min(
												BOX_DISPLAY_WIDTH,
												MAX_BOX_HEIGHT * crop.aspect,
											),
										}
							}
						>
							{crop.mode === 'free' ? (
								<img
									src={objectUrlRef.current ?? ''}
									alt="Førehandsvising"
									className="mx-auto max-h-80 w-full object-contain"
								/>
							) : (
								<canvas ref={previewCanvas} className="block w-full" />
							)}
							{crop.mode === 'crop' ? (
								<div className="pointer-events-none absolute inset-0 overflow-hidden">
									<div
										className={
											crop.circle ? 'absolute inset-0 rounded-full' : 'absolute'
										}
										style={{
											/* dark outside the safe zone */
											boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
											left: '9%',
											top: crop.circle ? '9%' : '6%',
											right: '9%',
											bottom: crop.circle ? '9%' : '6%',
											border: '2px solid rgba(255,255,255,0.8)',
										}}
									/>
								</div>
							) : null}
						</div>

						{crop.mode === 'crop' && (
							<>
								<label className="flex items-center gap-2">
									<span className="text-sm">Zoom</span>
									<input
										type="range"
										min={1}
										max={MAX_ZOOM}
										step={0.01}
										value={zoom}
										onChange={(e) => {
											const next = Number(e.target.value)
											setZoom(next)
											if (next <= 1) clampOffsets(0, 0)
										}}
										className="range range-xs flex-1"
									/>
								</label>
								<p className="text-base-content/60 text-xs">
									Hald det viktigaste (andlet, instrument, logoar) inne i det
									lyse, trygge området — det er garantert synleg. Dra for å
									flytte, skyv for å zoome.
								</p>
							</>
						)}
						{error && <p className="text-error">{error}</p>}
						<div className="flex flex-wrap items-center gap-2">
							<label className="btn btn-ghost btn-sm">
								{opening ? 'Opnar…' : 'Endre bilete'}
								<input
									type="file"
									accept={ACCEPT}
									disabled={opening}
									className="hidden"
									onChange={(e) => {
										const newFile = e.target.files?.[0]
										if (newFile) void handleFile(newFile)
									}}
								/>
							</label>
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								onClick={() => {
									revoke()
									setImg(null)
								}}
							>
								Fjern bilete
							</button>
							<button
								type="button"
								className="btn btn-primary ml-auto"
								disabled={busy}
								onClick={() => void confirm()}
							>
								{busy ? 'Handsamar…' : 'Bruk bilete'}
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
